import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router";
import { motion, useReducedMotion, useTransform } from "motion/react";
import { Tag, H1, Body } from "../components/shared/Typography";
import { GhostBtn } from "../components/shared/Buttons";
import { ParticleOrb } from "../components/shared/ParticleOrb";
import { Reveal } from "../components/shared/Reveal";
import { useHeroScrollFX } from "../hooks/useHeroScrollFX";
import { useSplitHeadlineReveal } from "../hooks/useSplitHeadlineReveal";
import { useLenis, scrollToId } from "../lib/lenis.tsx";
import { posthog } from "../lib/posthog";
import { MethodsMarquee } from "../components/home/iviz/MethodsMarquee";
import { StatPlayground } from "../components/home/iviz/playground/StatPlayground";
import { ChartGallery } from "../components/home/iviz/ChartGallery";
import { FlowHowItWorks } from "../components/home/iviz/FlowHowItWorks";
import { FlowBlockCatalog } from "../components/home/iviz/FlowBlockCatalog";
import { MeetMona } from "../components/home/iviz/MeetMona";
import { FlowVsNotebook } from "../components/home/iviz/FlowVsNotebook";
import { NotAnotherNotebook } from "../components/home/iviz/NotAnotherNotebook";
import { MeetMerlin } from "../components/home/iviz/MeetMerlin";
import { NotebookLibraries } from "../components/home/iviz/NotebookLibraries";
import { ComputeSelector } from "../components/home/iviz/ComputeSelector";
import { BuiltForTeams } from "../components/home/iviz/BuiltForTeams";
import { ProductTransition } from "../components/home/ProductTransition";
import { PromoOffer } from "../components/promo/PromoOffer";
import { BookDemo } from "../components/demo/BookDemo";

const IVIZ_APP_URL = "https://iviz.coharts.com/login";
const EASE = [0.22, 1, 0.36, 1] as const;

export function IvizPage() {
  const navigate = useNavigate();
  const lenis = useLenis();
  const reduced = useReducedMotion();
  const { ref: scatterRef, scrollYProgress } = useHeroScrollFX();
  const headingRef = useRef<HTMLHeadingElement>(null);
  useSplitHeadlineReveal(headingRef);
  const orbY = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [0, 60]);
  const heroTextOpacity = useTransform(scrollYProgress, [0, 0.7, 1], reduced ? [1, 1, 1] : [1, 1, 0]);
  const heroTextY = useTransform(scrollYProgress, [0, 0.7, 1], reduced ? [0, 0, 0] : [0, 0, -30]);
  const container = { hidden: {}, show: { transition: { staggerChildren: reduced ? 0 : 0.12, delayChildren: 0.1 } } };
  const item = {
    hidden: reduced ? { opacity: 0 } : { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };

  return (
    <div>
      <section ref={scatterRef} className="relative sm:min-h-[100dvh] flex items-start sm:items-center overflow-hidden bg-[#F5F8FC] dark:bg-[#050505] pt-20">
        <motion.div className="absolute left-0 right-0 top-20 bottom-0 z-0" style={{ y: orbY }}>
          <ParticleOrb variant="iviz-core" offsetX={1.6} scrollProgress={scrollYProgress} />
        </motion.div>

        <div className="relative z-10 max-w-[1800px] mx-auto px-6 lg:px-10 py-8 sm:py-16 w-full">
          <motion.div className="max-w-4xl" style={{ opacity: heroTextOpacity, y: heroTextY }}>
          <motion.div initial="hidden" animate="show" variants={container}>
            <motion.div variants={item}>
              <Tag>Intelligent Visualization · iViz</Tag>
            </motion.div>
            <H1 ref={headingRef} className="mt-5 mb-5">
              Explore with <span className="text-[#1E4FD8] dark:text-[#4A90D9]">intelligence.</span>
            </H1>
            <motion.p variants={item} className="font-display text-2xl sm:text-3xl font-medium tracking-[-0.01em] text-[#0A0A0F] dark:text-[#F5F5F7] mb-5">
              Rigorous statistics. With or without code.
            </motion.p>
            <motion.div variants={item}>
              <Body className="text-lg max-w-lg mb-3">
                Build analyses on a visual canvas with Flow, or write Python and R in Notebook. AI assistants Mona and Merlin work alongside you in each.
              </Body>
            </motion.div>
            <motion.p variants={item} className="text-sm font-medium text-[#0A0A0F] dark:text-white mb-8">
              Flow for people who don&apos;t code. Notebook for people who do.
            </motion.p>
            <motion.div variants={item} className="flex flex-wrap gap-4">
              <a
                href={IVIZ_APP_URL} target="_blank" rel="noopener noreferrer"
                onClick={() => posthog.capture("cta_clicked", { cta: "Launch iViz", location: "iviz_page_hero" })}
                className="inline-flex items-center gap-2 px-7 py-3.5 text-base font-medium rounded-full text-white dark:text-[#050505] bg-[#1E4FD8] dark:bg-[#4A90D9] transition-transform hover:scale-[1.02] active:scale-[0.98] active:brightness-90"
              >
                Launch iViz <ArrowRight size={16} />
              </a>
              <GhostBtn onClick={() => scrollToId(lenis, "flow")}>
                See how Flow works <ArrowRight size={16} />
              </GhostBtn>
            </motion.div>
          </motion.div>
          </motion.div>
        </div>
      </section>

      <div className="bg-[#FAFAFA] px-6 pt-10 pb-12 dark:bg-[#050505] lg:px-10">
        <Reveal className="mx-auto max-w-[1400px]">
          <PromoOffer location="iviz_page_after_hero" />
        </Reveal>
      </div>

      <div className="pb-8 bg-[#FAFAFA] dark:bg-[#050505]">
        <Reveal className="max-w-[1800px] mx-auto px-6 lg:px-10">
          <MethodsMarquee />
        </Reveal>
      </div>

      <div id="flow" className="py-16 bg-[#FAFAFA] dark:bg-[#050505] scroll-mt-[80px]">
        <Reveal className="max-w-[1800px] mx-auto px-6 lg:px-10">
          <FlowHowItWorks />
        </Reveal>
      </div>

      <div className="py-16 bg-[#FAFAFA] dark:bg-[#050505]">
        <Reveal className="max-w-[1800px] mx-auto px-6 lg:px-10">
          <StatPlayground />
        </Reveal>
      </div>

      <div className="py-16 bg-[#FAFAFA] dark:bg-[#050505]">
        <Reveal className="max-w-[1800px] mx-auto px-6 lg:px-10">
          <FlowBlockCatalog />
        </Reveal>
      </div>

      <div className="py-16 bg-[#FAFAFA] dark:bg-[#050505]">
        <Reveal className="max-w-[1800px] mx-auto px-6 lg:px-10">
          <ChartGallery />
        </Reveal>
      </div>

      <div className="py-16 bg-[#FAFAFA] dark:bg-[#050505]">
        <Reveal className="max-w-[1800px] mx-auto px-6 lg:px-10">
          <MeetMona />
        </Reveal>
      </div>

      <div className="py-16 bg-[#FAFAFA] dark:bg-[#050505]">
        <Reveal className="max-w-[1800px] mx-auto px-6 lg:px-10">
          <FlowVsNotebook />
        </Reveal>
      </div>

      <div className="py-16 bg-[#FAFAFA] dark:bg-[#050505]">
        <Reveal className="max-w-[1800px] mx-auto px-6 lg:px-10">
          <NotAnotherNotebook />
        </Reveal>
      </div>

      <div className="py-16 bg-[#FAFAFA] dark:bg-[#050505]">
        <Reveal className="max-w-[1800px] mx-auto px-6 lg:px-10">
          <MeetMerlin />
        </Reveal>
      </div>

      <div className="py-16 bg-[#FAFAFA] dark:bg-[#050505]">
        <Reveal className="max-w-[1800px] mx-auto px-6 lg:px-10">
          <NotebookLibraries />
        </Reveal>
      </div>

      <div id="compute" className="py-16 bg-[#FAFAFA] dark:bg-[#050505] scroll-mt-[80px]">
        <Reveal className="max-w-[1800px] mx-auto px-6 lg:px-10">
          <ComputeSelector />
        </Reveal>
      </div>

      <div className="pt-16 pb-28 bg-[#FAFAFA] dark:bg-[#050505]">
        <Reveal className="max-w-[1800px] mx-auto px-6 lg:px-10">
          <BuiltForTeams />
        </Reveal>
      </div>

      <section className="bg-[#FAFAFA] px-6 pb-24 dark:bg-[#050505] lg:px-10">
        <div className="mx-auto max-w-[1400px]">
          <BookDemo
            location="iviz_page_demo"
            variant="card"
            product="iviz"
            title="See how iViz fits your workflow."
            description="Tell us how you work with data, and we&apos;ll walk through a relevant iViz example together."
          />
        </div>
      </section>

      <ProductTransition onDiscoverForesight={() => navigate("/foresight")} />
    </div>
  );
}
